import NumberFlow, { useCanAnimate } from '@number-flow/react'
import { Link } from '@remix-run/react'
import { type NodeViewProps } from '@tiptap/core'
import { NodeViewWrapper } from '@tiptap/react'
import { motion } from 'framer-motion'
import { Copy, CopyCheck, Mail, Share } from 'lucide-react'
import { useState, useEffect } from 'react'
import { Button } from '#app/components/ui/button.js'
import { copyToClipboard } from '#app/utils/misc.js'

export const Component = (props: NodeViewProps) => {
	const { editor } = props
	let { jumpOne, referralLink, position } = props.extension.options
	const canAnimate = useCanAnimate()
	const { isEditable } = editor
	const {} = props.node.attrs
	const [linkCopied, setLinkCopied] = useState(false)
	const [animatedPosition, setAnimatedPosition] = useState(
		Math.max(position - 147, 0),
	)

	useEffect(() => {
		const timer = setTimeout(() => {
			setAnimatedPosition(position)
		}, 100)

		return () => clearTimeout(timer)
	}, [position])

	if (isEditable) {
		return (
			<NodeViewWrapper contenteditable="false">
				<div className="border-tt radius-tt-6 flex w-[420px] max-w-full flex-col items-center justify-center gap-8 p-6 text-center">
					<div className="flex flex-col items-center justify-center">
						<p className="p-tt !text-xl opacity-90">You're place</p>
						<h3 className="h-tt !text-mega">
							<span>
								<motion.span
									initial={false}
									className="inline-flex items-center px-0.5"
									layout={canAnimate}
									transition={{
										layout: { duration: 0.9, bounce: 0, type: 'spring' },
									}}
								>
									#
								</motion.span>
								<NumberFlow
									value={animatedPosition}
									className="~text-xl/4xl font-semibold"
									locales="en-US"
								/>
							</span>
						</h3>
						<p className="p-tt !text-xl opacity-90">on the waitlist</p>
					</div>
					<div className="hr-tt h-0.5 w-full"></div>
					<div className="flex flex-col items-center justify-center gap-2">
						<h2 className="p-tt text-center">
							Invite a friend
							<br />
							to jump to place{' '}
							<span className="inline-flex items-center">#{jumpOne}</span>
						</h2>
						<p className="p-tt">Your personal invite link</p>
						<p className="bg-accent-tt-10 rounded-sm px-1 backdrop-opacity-15">
							{referralLink}
						</p>
					</div>
					<div className="flex w-full gap-4">
						<Button
							className="button-tt radius-tt w-full"
							variant="customizer"
							type="button"
						>
							{linkCopied ? (
								<div className="flex items-center gap-1">
									<CopyCheck width={16} />
								</div>
							) : (
								<div className="flex items-center gap-1">
									<Copy width={16} />
								</div>
							)}
						</Button>
						<Button
							className="button-tt radius-tt w-full"
							variant="customizer"
							type="button"
						>
							<Share width={16} />
						</Button>
						<Button
							className="button-tt radius-tt w-full"
							variant="customizer"
							type="button"
						>
							<Mail width={16} />
						</Button>
						<Button
							className="button-tt radius-tt w-full"
							variant="customizer"
							type="button"
						>
							<div className="inline-block h-3.5 w-3.5">
								<svg
									role="img"
									viewBox="0 0 24 24"
									xmlns="http://www.w3.org/2000/svg"
								>
									<title>X</title>
									<path
										d="M18.901 1.153h3.68l-8.04 9.19L24 22.846h-7.406l-5.8-7.584-6.638 7.584H.474l8.6-9.83L0 1.154h7.594l5.243 6.932ZM17.61 20.644h2.039L6.486 3.24H4.298Z"
										fill="currentColor"
									/>
								</svg>
							</div>
						</Button>
					</div>
				</div>
			</NodeViewWrapper>
		)
	} else {
		const emailLink = `mailto:?body=${encodeURIComponent(`Sign up here: https://${referralLink}`)}`

		const tweetLink = `https://twitter.com/intent/tweet?text=Hey%2C%0A%0AJust%20found%20this%20awesome%20thing%2C%20here%27s%20my%20invite%20link%20if%20you%20would%20like%20to%20join%3A%0A%0A${referralLink}`

		const handleShare = async () => {
			if (navigator.share) {
				try {
					await navigator.share({
						title: 'Join me on the waitlist',
						text: 'Check out this awesome thing I found!',
						url: 'https://' + referralLink,
					})
				} catch (error) {
					console.error('Error sharing:', error)
				}
			} else {
				console.log('Web Share API not supported')
			}
		}

		return (
			<NodeViewWrapper>
				<div className="border-tt radius-tt-6 flex w-[420px] max-w-full flex-col items-center justify-center gap-8 p-6 text-center">
					<div className="flex flex-col items-center justify-center">
						<p className="p-tt !text-xl opacity-90">You're place</p>
						<div className="flex rounded-xl px-3">
							<h3 className="h-tt !text-mega">
								<span>
									<motion.span
										initial={false}
										className="inline-flex items-center px-0.5"
										layout={canAnimate}
										transition={{
											layout: { duration: 0.9, bounce: 0, type: 'spring' },
										}}
									>
										#
									</motion.span>
									<NumberFlow
										value={animatedPosition}
										className="~text-xl/4xl font-semibold"
										locales="en-US"
									/>
								</span>
							</h3>
						</div>
						<p className="p-tt !text-xl opacity-90">on the waitlist</p>
					</div>
					<div className="hr-tt h-0.5 w-full"></div>
					{position === 1 ? (
						<div className="flex flex-col items-center justify-center gap-2">
							<h2 className="p-tt">
								Invite more friends
								<br /> to stay first
							</h2>
							<p className="p-tt">Your personal invite link</p>
							<a
								href={'https://' + referralLink}
								className="bg-accent-tt-10 rounded-sm px-1 backdrop-opacity-15"
								target="_blank"
								rel="noreferrer"
							>
								{referralLink}
							</a>
						</div>
					) : (
						<div className="flex flex-col items-center justify-center gap-2">
							<h2 className="p-tt">
								Invite a friend to jump to
								<br />
								place #{jumpOne}
							</h2>
							<p className="p-tt">Your personal invite link</p>
							<a
								href={'https://' + referralLink}
								className="bg-accent-tt-10 rounded-sm px-1 backdrop-opacity-15"
								target="_blank"
								rel="noreferrer"
							>
								{referralLink}
							</a>
						</div>
					)}

					<div className="flex w-full gap-3">
						<Button
							variant="customizer"
							className="button-tt radius-tt w-full"
							onClick={() => {
								copyToClipboard(`https://${referralLink}`)
								setLinkCopied(true)

								setTimeout(() => {
									setLinkCopied(false)
								}, 2000)
							}}
						>
							{linkCopied ? (
								<>
									<CopyCheck width={16} />
								</>
							) : (
								<>
									<Copy width={16} />
								</>
							)}
						</Button>
						<Button
							variant="customizer"
							className="button-tt radius-tt w-full"
							type="button"
							onClick={handleShare}
						>
							<Share width={16} />
						</Button>
						<Button
							variant="customizer"
							className="button-tt radius-tt w-full"
							type="button"
							asChild
						>
							<Link to={emailLink} target="_blank" rel="noreferrer">
								<Mail width={16} />
							</Link>
						</Button>

						{tweetLink && (
							<Button
								variant="customizer"
								className="button-tt radius-tt w-full"
								type="button"
								asChild
							>
								<Link to={tweetLink} target="_blank" rel="noreferrer">
									<div className="inline-block h-3.5 w-3.5">
										<svg
											role="img"
											viewBox="0 0 24 24"
											xmlns="http://www.w3.org/2000/svg"
										>
											<title>X</title>
											<path
												d="M18.901 1.153h3.68l-8.04 9.19L24 22.846h-7.406l-5.8-7.584-6.638 7.584H.474l8.6-9.83L0 1.154h7.594l5.243 6.932ZM17.61 20.644h2.039L6.486 3.24H4.298Z"
												fill="currentColor"
											/>
										</svg>
									</div>
								</Link>
							</Button>
						)}
					</div>
				</div>
			</NodeViewWrapper>
		)
	}
}

export default Component
